// Klient REST API: fetch nad /api/v1 se základem z VITE_API_URL. Chyby serveru mají vždy tvar
// { kod, zprava, detaily? } (schéma Chyba v api/openapi.yaml); selhání sítě a neplatné odpovědi
// převádí klient na stejný tvar, aby UI mělo vždy co ukázat uživateli.
//
// Přihlášení: relace je v HttpOnly cookie `ecoser_relace` na stejném původu (ve vývoji přes proxy
// Vite /api) — prohlížeč ji posílá sám, klient žádný token nedrží. Každý požadavek nese hlavičku
// `X-Requested-With: ECOser` (backend ji u mutací bere jako doplněk k Sec-Fetch-Site / Origin).

import { hlasSelhaniSite, hlasUspechSite } from '../offline/sit';

/** Chybová odpověď API — shodná se schématem `Chyba` v api/openapi.yaml. */
export interface Chyba {
  /** Strojový kód, např. `stav.neplatny_prechod`, `nemate_opravneni`, `neprihlasen`. */
  kod: string;
  /** Česky pro uživatele: co se stalo a co udělat. */
  zprava: string;
  /** U validace: které pole a proč. */
  detaily?: { pole?: string; zprava?: string }[];
  /** U 403: lidský důvod „proč to nejde“ (08 §1 bod 1) — pro text vedle neaktivního prvku. */
  duvod?: string;
  /** U 403: klíč akce, která byla zamítnuta (např. `zamestnanec.ucet`); u 409 navržený další krok (`overit_znovu`, `potvrdit_karta_neprosla`). */
  akce?: string;
}

/** Chyba vyhozená klientem — nese `kod` a `zprava` pro UI a HTTP status pro logiku. */
export class ChybaApi extends Error implements Chyba {
  readonly kod: string;
  readonly zprava: string;
  readonly detaily?: Chyba['detaily'];
  readonly duvod?: string;
  readonly akce?: string;
  /** HTTP status; 0 = odpověď vůbec nedorazila (síť, CORS, timeout). */
  readonly status: number;
  /** Celé tělo chybové odpovědi — další pole podle kontraktu (např. `duplicitni` u 409 duplicity). */
  readonly telo: Readonly<Record<string, unknown>>;

  constructor(chyba: Chyba, status: number) {
    super(chyba.zprava);
    this.name = 'ChybaApi';
    this.kod = chyba.kod;
    this.zprava = chyba.zprava;
    this.detaily = chyba.detaily;
    this.duvod = chyba.duvod;
    this.akce = chyba.akce;
    this.status = status;
    this.telo = chyba as unknown as Record<string, unknown>;
  }
}

export type Metoda = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface MoznostiPozadavku {
  metoda?: Metoda;
  /** Tělo požadavku; serializuje se jako JSON (`Blob` a `ArrayBuffer` se pošlou beze změny). */
  telo?: unknown;
  signal?: AbortSignal;
  hlavicky?: Record<string, string>;
  /** Požadavek má přežít opuštění stránky (uložení nastavení při zavření záložky). */
  keepalive?: boolean;
  /**
   * Nehlásit selhání sítě offline režimu (4.5) — pro dotahování na pozadí (balíček dat na den), které smí
   * tiše selhat, aniž by se aplikace prohlásila za „bez signálu“.
   */
  bezHlaseniSite?: boolean;
}

/** Možnosti pro zkratky podle metody (metoda a tělo jsou dané). */
type MoznostiZkratky = Omit<MoznostiPozadavku, 'metoda' | 'telo'>;

const PREFIX_API = '/api/v1';

/** Hlavička, kterou nese každý požadavek z aplikace (backend ji kontroluje u mutací). */
export const HLAVICKA_POZADAVKU = { 'X-Requested-With': 'ECOser' } as const;

/** Základ URL bez lomítka na konci; prázdný = stejný původ (ve vývoji jde přes proxy Vite). */
export function zakladUrl(): string {
  return (import.meta.env.VITE_API_URL ?? '').trim().replace(/\/+$/, '');
}

/** Sestaví absolutní URL endpointu, `cesta` je relativní k /api/v1 (např. `/zdravi`). */
export function urlEndpointu(cesta: string): string {
  const normalizovana = cesta.startsWith('/') ? cesta : `/${cesta}`;
  return `${zakladUrl()}${PREFIX_API}${normalizovana}`;
}

/** Obecná věta serveru u validace (`chyby.ts`) — sama o sobě na nic neukazuje. */
const OBECNA_VALIDACE = /Opravte označená pole\.?\s*$/;

/** Věta jednoho detailu validace i s polem, ke kterému patří („„x“ není platná adresa (pole email).“). */
function vetaDetailu(d: { pole?: string; zprava?: string }): string | null {
  const zprava = d.zprava?.trim();
  if (!zprava) return null;
  const veta = /[.!?]$/.test(zprava) ? zprava : `${zprava}.`;
  return d.pole ? `${veta.slice(0, -1)} (pole ${d.pole}).` : veta;
}

/**
 * Důvod „proč to nejde“ z chyby: u 403 `duvod`, jinak `zprava`. Obecná věta validace („Opravte označená pole.“)
 * se nahradí konkrétními větami z `detaily` i s názvem pole — okno, které pole nezvýrazňuje, by jinak ukázalo na
 * nic (nález zadavatele 27. 10. 2026: „nevidím žádná označená pole“).
 */
export function duvodChyby(chyba: unknown): string {
  const c = naChybu(chyba);
  const zaklad = c.duvod ?? c.zprava;
  if (c.kod !== 'validace' || !OBECNA_VALIDACE.test(zaklad)) return zaklad;
  const vety = (c.detaily ?? []).flatMap((d) => vetaDetailu(d) ?? []);
  if (vety.length === 0) return zaklad;
  return `${zaklad.replace(OBECNA_VALIDACE, '').trim()} ${vety.join(' ')}`.trim();
}

/**
 * Chyba serveru rozdělená na pole formuláře (`mapa`: `detaily[].pole` → klíč pole v okně) a zbytek pro hlášku
 * nad formulářem: detaily bez pole v okně jsou ve zbytku i s názvem pole; když jsou všechny detaily u polí,
 * zbytek řekne, kolik polí je označeno.
 */
export function chybyPoli<K extends string>(
  chyba: unknown,
  mapa: Readonly<Record<string, K>>,
): { pole: Partial<Record<K, string>>; zbytek: string } {
  const c = naChybu(chyba);
  const pole: Partial<Record<K, string>> = {};
  const mimo: string[] = [];
  for (const d of c.detaily ?? []) {
    const klic = d.pole !== undefined ? mapa[d.pole] : undefined;
    if (klic && d.zprava) {
      if (!pole[klic]) pole[klic] = d.zprava;
    } else {
      const veta = vetaDetailu(d);
      if (veta) mimo.push(veta);
    }
  }
  const zaklad = c.duvod ?? c.zprava;
  if (mimo.length > 0) {
    return {
      pole,
      zbytek: `${zaklad.replace(OBECNA_VALIDACE, '').trim()} ${mimo.join(' ')}`.trim(),
    };
  }
  const pocet = Object.keys(pole).length;
  if (pocet > 0 && OBECNA_VALIDACE.test(zaklad)) {
    return {
      pole,
      zbytek: `${zaklad.replace(OBECNA_VALIDACE, '').trim()} Opravte ${pocet === 1 ? 'označené pole' : 'označená pole'}.`,
    };
  }
  return { pole, zbytek: zaklad };
}

/**
 * Důvod chyby i s detaily validace u polí, která formulář nezobrazuje (`zobrazena` = pole s vlastním zvýrazněním).
 * Bez toho by „Opravte označená pole“ ukázalo na nic — třeba když server odmítne údaj, který doplnil sám
 * (nález zadavatele 24. 10. 2026: nabídka od jednatele).
 */
export function duvodChybyMimoPole(chyba: unknown, zobrazena: readonly string[]): string {
  const c = naChybu(chyba);
  const skryte = (c.detaily ?? []).flatMap((d) => {
    const zprava = d.zprava?.trim();
    return zprava && !(d.pole !== undefined && zobrazena.includes(d.pole)) ? [zprava] : [];
  });
  const zaklad = c.duvod ?? c.zprava;
  if (skryte.length === 0) return zaklad;
  const veta = (z: string) => (/[.!?]$/.test(z) ? z : `${z}.`);
  return `${zaklad} ${skryte.map(veta).join(' ')}`;
}

/** Další pole chybové odpovědi (`duplicitni` u 409 …); u jiných chyb undefined. */
export function poleChyby<T = unknown>(chyba: unknown, pole: string): T | undefined {
  return chyba instanceof ChybaApi ? (chyba.telo[pole] as T | undefined) : undefined;
}

/** Zpráva validační chyby pro konkrétní pole (`detaily[].pole`), jinak undefined. */
export function chybaPole(chyba: unknown, pole: string): string | undefined {
  const c = naChybu(chyba);
  return c.detaily?.find((d) => d.pole === pole)?.zprava;
}

function jeChyba(hodnota: unknown): hodnota is Chyba {
  return (
    typeof hodnota === 'object' &&
    hodnota !== null &&
    typeof (hodnota as Chyba).kod === 'string' &&
    typeof (hodnota as Chyba).zprava === 'string'
  );
}

/** Tělo odpovědi: `{ json: true, data }` u platného JSON, `{ json: false, data: text }` jinak. */
async function precistTelo(odpoved: Response): Promise<{ json: boolean; data: unknown }> {
  if (odpoved.status === 204) {
    return { json: true, data: undefined };
  }
  const text = await odpoved.text();
  if (text === '') {
    return { json: true, data: undefined };
  }
  try {
    return { json: true, data: JSON.parse(text) as unknown };
  } catch {
    return { json: false, data: text };
  }
}

function chybaZOdpovedi(odpoved: Response, telo: unknown): ChybaApi {
  if (jeChyba(telo)) {
    return new ChybaApi(telo, odpoved.status);
  }
  const zprava =
    odpoved.status >= 500
      ? 'Server hlásí chybu. Zkuste to za chvíli znovu; pokud potíže trvají, dejte vědět správci.'
      : `Server odpověděl neočekávaně (HTTP ${odpoved.status}).`;
  return new ChybaApi({ kod: 'server.neplatna_odpoved', zprava }, odpoved.status);
}

// --- Úspěšné zápisy (hlídání rozepsaných oken, vlak S27) ---------------------------------------------------------
// Okno, které se ukládá a zůstane otevřené (provozní deník, karta vozidla se servisním plánem), po uložení nic
// rozepsaného nemá — `useRozepsaneOkno` proto po úspěšném zápisu zapomene „sáhnuto“ a při zavření se už neptá.
// Náhled (`?nahled=true`) a výpočty bez zápisu (POST …/vypocet, /kontrola, /navrh, /kalkulace, /overit) se nepočítají.
const POSLUCHACI_ZAPISU = new Set<() => void>();
const BEZ_ZAPISU =
  /[?&]nahled=(true|1)\b|\/(vypocet|kontrola|navrh|navrh-[a-z-]+|kalkulace|overit|upravit-navrh)(\?|$)/;

/** Přihlásí posluchače úspěšných zápisů (POST/PUT/PATCH/DELETE mimo náhled); vrací odhlášení. */
export function poslouchejZapisy(posluchac: () => void): () => void {
  POSLUCHACI_ZAPISU.add(posluchac);
  return () => {
    POSLUCHACI_ZAPISU.delete(posluchac);
  };
}

function oznamZapis(metoda: Metoda, cesta: string): void {
  if (metoda === 'GET' || BEZ_ZAPISU.test(cesta)) return;
  for (const p of [...POSLUCHACI_ZAPISU]) p();
}

/** Obecný požadavek na API. Vrací rozparsované JSON tělo, u 204 `undefined`. */
export async function pozadavek<T>(cesta: string, moznosti: MoznostiPozadavku = {}): Promise<T> {
  const { metoda = 'GET', telo, signal, hlavicky, keepalive, bezHlaseniSite } = moznosti;
  const hlavickyPozadavku: Record<string, string> = {
    Accept: 'application/json',
    ...HLAVICKA_POZADAVKU,
    ...hlavicky,
  };
  // Surová data (záznam hlasu pro diktát) jdou beze změny s `Content-Type` z `hlavicky`; formulář (`FormData`, nahrání
  // certifikátu datové schránky PUT multipart) také — hranici si doplní prohlížeč; vše ostatní jako JSON.
  const surove = telo instanceof Blob || telo instanceof ArrayBuffer || telo instanceof FormData;
  if (telo !== undefined && !surove) {
    hlavickyPozadavku['Content-Type'] = 'application/json';
  }

  let odpoved: Response;
  try {
    odpoved = await fetch(urlEndpointu(cesta), {
      method: metoda,
      headers: hlavickyPozadavku,
      body: telo === undefined ? undefined : surove ? telo : JSON.stringify(telo),
      // Relace je v cookie stejného původu; prohlížeč ji přiloží sám.
      credentials: 'same-origin',
      signal,
      keepalive,
    });
  } catch (chyba) {
    if (chyba instanceof DOMException && chyba.name === 'AbortError') {
      throw chyba;
    }
    // Tablet bez signálu (4.5): fronta si to pamatuje a zápisy schová do offline fronty.
    if (!bezHlaseniSite) hlasSelhaniSite();
    throw new ChybaApi(
      {
        kod: 'sit.nedostupna',
        zprava: 'Server neodpovídá. Zkontrolujte připojení k internetu a zkuste to znovu.',
      },
      0,
    );
  }
  if (!bezHlaseniSite) hlasUspechSite();

  const { json, data } = await precistTelo(odpoved);
  if (!odpoved.ok) {
    throw chybaZOdpovedi(odpoved, data);
  }
  oznamZapis(metoda, cesta);
  if (!json) {
    // Např. HTML místo JSON (špatně nasměrovaný server, přihlašovací stránka proxy) — nikdy
    // nevracet jako data, UI by s textem pracovalo jako s objektem.
    throw new ChybaApi(
      {
        kod: 'server.neplatna_odpoved',
        zprava: 'Server odpověděl neočekávaně (odpověď není JSON). Dejte vědět správci.',
      },
      odpoved.status,
    );
  }
  return data as T;
}

/** Zkratky podle metody; `cesta` je relativní k /api/v1. */
export const api = {
  get: <T>(cesta: string, moznosti?: MoznostiZkratky) =>
    pozadavek<T>(cesta, { ...moznosti, metoda: 'GET' }),
  post: <T>(cesta: string, telo?: unknown, moznosti?: MoznostiZkratky) =>
    pozadavek<T>(cesta, { ...moznosti, metoda: 'POST', telo }),
  put: <T>(cesta: string, telo?: unknown, moznosti?: MoznostiZkratky) =>
    pozadavek<T>(cesta, { ...moznosti, metoda: 'PUT', telo }),
  patch: <T>(cesta: string, telo?: unknown, moznosti?: MoznostiZkratky) =>
    pozadavek<T>(cesta, { ...moznosti, metoda: 'PATCH', telo }),
  delete: <T>(cesta: string, moznosti?: MoznostiZkratky) =>
    pozadavek<T>(cesta, { ...moznosti, metoda: 'DELETE' }),
};

/** Převede libovolnou vyhozenou hodnotu na { kod, zprava } pro zobrazení v UI. */
export function naChybu(chyba: unknown): Chyba {
  if (chyba instanceof ChybaApi) {
    return {
      kod: chyba.kod,
      zprava: chyba.zprava,
      detaily: chyba.detaily,
      duvod: chyba.duvod,
      akce: chyba.akce,
    };
  }
  if (chyba instanceof Error) {
    return { kod: 'klient.neznama_chyba', zprava: chyba.message };
  }
  return { kod: 'klient.neznama_chyba', zprava: 'Něco se nepovedlo. Zkuste to znovu.' };
}
